import { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';

import type { InsightScreenshotComment } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/insight-screenshot';

interface CommentsPanelProps {
  comments?: InsightScreenshotComment[];
  loading: boolean;
  selectedCommentId: string | number | null;
  onCommentClick: (commentId: string | number) => void;
}

const CommentsPanel = ({
  comments = [],
  loading,
  selectedCommentId,
  onCommentClick,
}: CommentsPanelProps): React.JSX.Element => {
  const selectedCommentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedCommentId && selectedCommentRef.current) {
      selectedCommentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedCommentId]);
  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: 'common.white',
          borderRadius: '0.5rem',
          borderWidth: '1px',
          p: 3,
        }}
      >
        <h3 className="text-lg font-semibold mb-4 text-navy-800">Insights</h3>
        <Loader loading={true} />
      </Box>
    );
  }

  if (comments.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: 'common.white',
          borderRadius: '0.5rem',
          borderWidth: '1px',
          p: 3,
        }}
      >
        <h3 className="text-lg font-semibold mb-4">Insights</h3>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <p className="text-navy-800">No insights yet</p>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: '0.375rem',
        borderWidth: '1px',
        borderColor: 'tokens.navy.100',
        height: '100%',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <h3 className="font-semibold text-navy-800">
          Insights ({comments.length})
        </h3>
      </Box>

      <div className="max-h-[500px] overflow-y-auto custom-scroll mr-1 px-4 py-2.5 flex flex-col gap-2">
        {comments.map((comment, index) => {
          const isSelected = selectedCommentId === comment.id;
          const commentNumber = index + 1;

          return (
            <button
              key={comment.id ?? `comment-${commentNumber}`}
              ref={isSelected ? selectedCommentRef : null}
              className={classNames(
                'w-full text-left px-3 py-2.5 border border-navy-200 rounded-md cursor-pointer transition-all duration-200',
                {
                  'bg-indigo-05 shadow !border-navy-500': isSelected,
                  'hover:bg-gray-50': !isSelected,
                }
              )}
              onClick={() => {
                if (comment.id !== undefined) {
                  onCommentClick(comment.id);
                }
              }}
              type="button"
            >
              <Stack
                direction="row"
                sx={{ alignItems: 'flex-start', gap: 0.75 }}
              >
                <div
                  className={classNames(
                    'size-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm',
                    {
                      'bg-navy-800': isSelected,
                      'bg-navy-500': !isSelected,
                    }
                  )}
                >
                  {commentNumber}
                </div>

                <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
                  <p className="text-gray-700 text-sm break-words">
                    {comment.message}
                  </p>
                </Box>
              </Stack>
            </button>
          );
        })}
      </div>
    </Box>
  );
};

export default CommentsPanel;
